import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { LockIcon } from "../../icons";
import { isValidPhoneNumber } from 'react-phone-number-input';
import { InputPhone } from '../../entities/common/controls/InputPhone';
import { normalizeAndValidateEmail } from '../utils';
import ExternalValidationExtras from './externalValidationExtras';
import { validateMask } from '../../entities/common/utils';
// Los tipos de lo que manda el server, los mismos de la página y de las pantallas propias.
import type {
  ExternalValidationFieldConfig as FieldConfig,
  ExternalValidationGroupConfig as GroupConfig,
  ExternalValidationLookupOption as LookupOption,
  ExternalValidationSubmitResult,
  ExternalValidationTenantInfo as TenantInfo,
} from './externalValidationTypes';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/graphql', '') || 'http://localhost:4000';

interface ExternalValidationFormProps {
  entityData: Record<string, any>;
  fields: FieldConfig[];
  groups: GroupConfig[];
  editableFields: string[];
  lookups?: Record<string, LookupOption[]>;
  vid: string;
  sig: string;
  tenantInfo: TenantInfo;
  onSubmitted: (result: ExternalValidationSubmitResult) => void;
}

const DEFAULT_LANG = (process.env.REACT_APP_DEFAULT_LANGUAGE || 'es') as string;

function getLabel(labels: Record<string, string>): string {
  return labels[DEFAULT_LANG] || labels.es || labels.en || Object.values(labels)[0] || '';
}

const VALIDATION_MSG: Record<string, { required: string; email: string; format: string; phone: string; fix: string }> = {
  es: { required: 'Campo requerido', email: 'Email inválido', format: 'Formato inválido', phone: 'Teléfono inválido', fix: 'Hay que corregir los campos marcados antes de continuar' },
  en: { required: 'Required field', email: 'Invalid email', format: 'Invalid format', phone: 'Invalid phone number', fix: 'Fix the highlighted fields before continuing' },
  pt: { required: 'Campo obrigatório', email: 'Email inválido', format: 'Formato inválido', phone: 'Telefone inválido', fix: 'Corrija os campos destacados antes de continuar' },
};
const MSG = VALIDATION_MSG[DEFAULT_LANG] ?? VALIDATION_MSG.es;

/**
 * Lo que vale un campo numérico (Int o Decimal) mientras la persona teclea: el
 * número, también un 0; un texto vacío o que no es un número queda en blanco
 * (''), que el formulario y el server tratan como faltante.
 */
export function numberFieldValue(type: 'Int' | 'Decimal', text: string): number | '' {
  const value = type === 'Int' ? parseInt(text, 10) : parseFloat(text);
  return Number.isNaN(value) ? '' : value;
}

export const ExternalValidationForm: React.FC<ExternalValidationFormProps> = ({
  entityData,
  fields,
  groups,
  editableFields,
  lookups,
  vid,
  sig,
  tenantInfo,
  onSubmitted,
}) => {
  const editableSet = new Set(editableFields);
  const fieldsByName: Record<string, FieldConfig> = {};
  for (const f of fields) {
    fieldsByName[f.name] = f;
  }

  // Initialize form data from entityData
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    for (const f of fields) {
      if (editableSet.has(f.name)) {
        if (f.type === 'Ref') {
          initial[f.name] = entityData[`${f.name}_id`] ?? '';
        } else {
          initial[f.name] = entityData[f.name] ?? '';
        }
      }
    }
    return initial;
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Limpiar el error del campo al editarlo.
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  // Valida TODOS los campos editables y acumula los errores (requerido + email +
  // máscara/formato). Los enum quedan restringidos por el Select. Las reglas de
  // negocio (self-publisher, lock, etc.) las enfuerza el server y vuelven mapeadas.
  const validateAll = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    for (const f of fields) {
      if (!editableSet.has(f.name)) continue;
      const raw = formData[f.name];
      const isEmpty = raw === '' || raw === null || raw === undefined;
      if (f.required && isEmpty) {
        errs[f.name] = MSG.required;
        continue;
      }
      if (isEmpty) continue;
      const str = String(raw);
      // Mismos validadores que el form generado de jupiter-client.
      if (f.email && normalizeAndValidateEmail(str) === null) {
        errs[f.name] = MSG.email;
        continue;
      }
      if (f.masks && f.masks.length > 0 && !f.masks.some((m) => validateMask(str, { mask: m }))) {
        errs[f.name] = MSG.format;
        continue;
      }
      if (f.phone && !isValidPhoneNumber(str)) {
        errs[f.name] = MSG.phone;
        continue;
      }
    }
    return errs;
  };

  const resetCompositeChildren = (parentFieldName: string) => {
    for (const f of fields) {
      if (f.compositeWith === parentFieldName) {
        setFormData(prev => ({ ...prev, [f.name]: '' }));
      }
    }
  };

  // Comprobación que puede registrar el agregado del proyecto
  // (externalValidationExtras): corre con los campos ya validados y puede
  // frenar el envío, incluso esperando una decisión del usuario.
  const beforeSubmitRef = useRef<(() => boolean | Promise<boolean>) | null>(null);

  const handleSubmit = async () => {
    // 1) Validación client-side: acumular TODOS los errores antes de enviar.
    const localErrors = validateAll();
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(MSG.fix);
      return;
    }

    // 2) Lo que el proyecto haya sumado al formulario tiene la última palabra.
    if (beforeSubmitRef.current && !(await beforeSubmitRef.current())) return;

    setFieldErrors({});
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/api/public/external-validation/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vid, sig, data: formData }),
      });

      const data = await response.json();

      if (data.success) {
        onSubmitted({ changes: data.changes || 0, tenantInfo: data.tenantInfo });
      } else if (data.error === 'validation_failed') {
        // 2) Error de negocio del server: mapear al campo si trae `field`,
        //    si no mostrarlo global. El usuario corrige y reintenta.
        if (data.field) {
          setFieldErrors({ [data.field]: data.message || MSG.format });
        }
        setError(data.field ? MSG.fix : (data.message || 'unknown_error'));
      } else {
        setError(data.error || 'unknown_error');
      }
    } catch {
      setError('network_error');
    } finally {
      setSubmitting(false);
    }
  };

  // Las dos instancias del agregado del proyecto comparten estas funciones.
  const patchForm = (valores: Record<string, any>) => {
    // Solo los campos editables: el agregado no puede cambiar lo que el
    // formulario decidió mostrar como fijo.
    setFormData((prev) => {
      const next = { ...prev };
      for (const [campo, valor] of Object.entries(valores)) {
        if (editableSet.has(campo)) next[campo] = valor;
      }
      return next;
    });
    setFieldErrors({});
  };
  const registrarComprobacion = (check: (() => boolean | Promise<boolean>) | null) => {
    beforeSubmitRef.current = check;
  };

  const renderField = (fieldConfig: FieldConfig) => {
    const isEditable = editableSet.has(fieldConfig.name);
    const value = isEditable ? formData[fieldConfig.name] : entityData[fieldConfig.name];
    const label = getLabel(fieldConfig.label);

    // Read-only display
    if (!isEditable) {
      let displayValue = value ?? '';
      if (fieldConfig.type === 'Ref') {
        const refData = entityData[fieldConfig.name];
        displayValue = refData
          ? (refData[`${fieldConfig.refDisplayField}_${DEFAULT_LANG}`] || refData[fieldConfig.refDisplayField || 'name'] || refData.id || '')
          : '';
      }
      return (
        <TextField
          key={fieldConfig.name}
          label={label}
          value={displayValue}
          disabled
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: <LockIcon fontSize="small" color="disabled" />,
          }}
        />
      );
    }

    // Editable fields by type
    const fieldError = fieldErrors[fieldConfig.name];

    // Teléfono: mismo componente que el form normal (banderitas + input tel).
    if (fieldConfig.phone) {
      return (
        <Box key={fieldConfig.name} sx={{ mb: 2 }}>
          <InputPhone
            label={label}
            value={value ?? ''}
            error={!!fieldError}
            helperText={fieldError}
            onChange={(v: string) => handleChange(fieldConfig.name, v)}
          />
        </Box>
      );
    }

    switch (fieldConfig.type) {
      case 'Boolean':
        return (
          <FormControlLabel
            key={fieldConfig.name}
            control={
              <Switch
                checked={!!value}
                onChange={(e) => handleChange(fieldConfig.name, e.target.checked)}
              />
            }
            label={label}
            sx={{ mb: 2, display: 'flex' }}
          />
        );

      case 'Enum':
        return (
          <FormControl key={fieldConfig.name} fullWidth size="small" sx={{ mb: 2 }} error={!!fieldError}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={value ?? ''}
              onChange={(e) => handleChange(fieldConfig.name, e.target.value)}
              label={label}
            >
              {fieldConfig.nullable && (
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
              )}
              {fieldConfig.options?.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {getLabel(opt.label)}
                </MenuItem>
              ))}
            </Select>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );

      case 'Int':
      case 'Decimal':
        return (
          <TextField
            key={fieldConfig.name}
            label={label}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleChange(fieldConfig.name, numberFieldValue(fieldConfig.type as 'Int' | 'Decimal', e.target.value))}
            required={fieldConfig.required}
            error={!!fieldError}
            helperText={fieldError}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        );

      case 'Date':
        return (
          <TextField
            key={fieldConfig.name}
            label={label}
            type="date"
            value={value ? String(value).substring(0, 10) : ''}
            onChange={(e) => handleChange(fieldConfig.name, e.target.value || null)}
            required={fieldConfig.required}
            error={!!fieldError}
            helperText={fieldError}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'DateTime':
        return (
          <TextField
            key={fieldConfig.name}
            label={label}
            type="datetime-local"
            value={value ? String(value).substring(0, 16) : ''}
            onChange={(e) => handleChange(fieldConfig.name, e.target.value || null)}
            required={fieldConfig.required}
            error={!!fieldError}
            helperText={fieldError}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'Ref': {
        const entityLookup = lookups?.[fieldConfig.refEntity || ''] || [];
        const parentValue = fieldConfig.compositeWith ? formData[fieldConfig.compositeWith] : null;
        const filteredOptions = parentValue
          ? entityLookup.filter(opt => opt.parentId === parentValue)
          : entityLookup;

        return (
          <FormControl key={fieldConfig.name} fullWidth size="small" sx={{ mb: 2 }} error={!!fieldError}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={value ?? ''}
              onChange={(e) => {
                handleChange(fieldConfig.name, e.target.value);
                resetCompositeChildren(fieldConfig.name);
              }}
              label={label}
            >
              {fieldConfig.nullable && (
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
              )}
              {filteredOptions.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.displayId}
                </MenuItem>
              ))}
            </Select>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );
      }

      // File, SubEntity — display only
      case 'File':
      case 'SubEntity':
        return (
          <TextField
            key={fieldConfig.name}
            label={label}
            value={value != null ? String(value) : ''}
            disabled
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        );

      // String and everything else
      default:
        return (
          <TextField
            key={fieldConfig.name}
            label={label}
            value={value ?? ''}
            onChange={(e) => handleChange(fieldConfig.name, e.target.value)}
            required={fieldConfig.required}
            error={!!fieldError}
            helperText={fieldError}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        );
    }
  };

  const renderGroup = (group: GroupConfig) => {
    const groupLabel = getLabel(group.label);
    const groupFields = group.fields
      .map(fname => fieldsByName[fname])
      .filter(Boolean);

    if (groupFields.length === 0) return null;

    return (
      <Box key={group.name} sx={{ mb: 3 }}>
        {groupLabel && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {groupLabel}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </>
        )}
        {groupFields.map(f => renderField(f))}
      </Box>
    );
  };

  // Determine rendering: if groups exist, use them; otherwise render all fields flat
  const hasGroups = groups && groups.length > 0;

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
        {tenantInfo.tenantName
          ? `${tenantInfo.tenantName}`
          : ''
        }
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
        {DEFAULT_LANG === 'pt'
          ? 'Verifique seu e-mail e revise as informações. Você pode corrigir o que for necessário.'
          : DEFAULT_LANG === 'en'
          ? 'Verify your email and review the information. You can correct anything that needs updating.'
          : 'Verificá tu email y revisá la información. Podés corregir lo que sea necesario.'
        }
      </Typography>

      {/* Punto de extensión del proyecto (stub estable), arriba: lo que la
          persona debería ver ANTES de ponerse a completar los campos. */}
      <ExternalValidationExtras
        slot="top"
        entityData={entityData}
        formData={formData}
        vid={vid}
        sig={sig}
        lookups={lookups}
        patchForm={patchForm}
        registerBeforeSubmit={registrarComprobacion}
      />

      {hasGroups
        ? groups.map(g => renderGroup(g))
        : fields.map(f => renderField(f))
      }

      {/* `error` puede traer un código crudo (not_found, internal_error, ...) o un
          mensaje ya legible (MSG.fix de la validación local, message de una
          ValidationError del server). Los códigos snake_case se mapean a un texto
          genérico; un mensaje legible se muestra tal cual — si no, un error de
          validación local parece una falla del sistema. */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error === 'network_error'
            ? (DEFAULT_LANG === 'en' ? 'Connection error. Please try again.' : DEFAULT_LANG === 'pt' ? 'Erro de conexão. Tente novamente.' : 'Error de conexión. Volvé a intentar.')
            : error === 'expired'
            ? (DEFAULT_LANG === 'en' ? 'This link has expired.' : DEFAULT_LANG === 'pt' ? 'Este link expirou.' : 'Este enlace ha expirado.')
            : /^[a-z0-9_]+$/.test(error)
            ? (DEFAULT_LANG === 'en' ? 'An error occurred. Please try again.' : DEFAULT_LANG === 'pt' ? 'Ocorreu um erro. Tente novamente.' : 'Ocurrió un error. Volvé a intentar.')
            : error
          }
        </Alert>
      )}

      {/* Punto de extensión del proyecto (stub estable), abajo: lo que
          corresponde al momento de confirmar. */}
      <ExternalValidationExtras
        slot="bottom"
        entityData={entityData}
        formData={formData}
        vid={vid}
        sig={sig}
        lookups={lookups}
        patchForm={patchForm}
        registerBeforeSubmit={registrarComprobacion}
      />

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ px: 4, py: 1.5 }}
        >
          {submitting
            ? <CircularProgress size={24} color="inherit" />
            : (DEFAULT_LANG === 'pt'
                ? 'Verificar e-mail e confirmar dados'
                : DEFAULT_LANG === 'en'
                ? 'Verify email and confirm data'
                : 'Verificar email y confirmar datos'
              )
          }
        </Button>
      </Box>
    </Box>
  );
};
